import React, {useEffect, useRef, useState} from "react";
import {Box, ButtonBase, Collapse, Typography} from "@mui/material";
import ChevronRightIcon from "~/icons/svg/ChevronRightIcon.svg?react";
import {
    bodySx,
    getChevronSx,
    getTitleSx,
    headerSx,
    sectionSx,
} from "~/pages/chat-agent/components/AgentActivity/agentActivity.styles";

type Props = {
    title: React.ReactNode;
    /** The turn is still streaming into this section — an opened body follows new content. */
    isActive: boolean;
    /** Animates the title while work is in progress; defaults to `isActive`. */
    isWorking?: boolean;
    /** Changes whenever the body grows; an active, expanded body scrolls to keep it in view. */
    scrollKey?: unknown;
    /** Opens the section while true and folds it when it turns false; defaults to `isActive`. */
    autoOpen?: boolean;
    children: React.ReactNode;
};

/**
 * Collapsible agent-activity block (thinking, tool calls) inside an agent turn. Open while the turn
 * streams, folded once it ends and on reload; the user can toggle it either way.
 */
export const CollapsibleSection: React.FC<Props> = ({
                                                        title,
                                                        isActive,
                                                        isWorking = isActive,
                                                        scrollKey,
                                                        autoOpen = isActive,
                                                        children,
                                                    }) => {
    const [expanded, setExpanded] = useState(autoOpen);
    const [prevAutoOpen, setPrevAutoOpen] = useState(autoOpen);
    if (prevAutoOpen !== autoOpen) {
        setPrevAutoOpen(autoOpen);
        setExpanded(autoOpen);
    }
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isActive || !expanded || !scrollRef.current) return;
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [scrollKey, isActive, expanded]);

    return (
        <Box className="chat-message message-fade-in" sx={sectionSx}>
            <ButtonBase
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                sx={headerSx}
            >
                <Box component={ChevronRightIcon} sx={getChevronSx(expanded)}/>
                <Typography variant="body2" sx={getTitleSx(isWorking)}>
                    {title}
                </Typography>
            </ButtonBase>
            <Collapse in={expanded} unmountOnExit>
                <Box ref={scrollRef} sx={bodySx}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};
